import React from 'react';
import { motion } from 'framer-motion';
import { 
  MessageSquare, Send, Zap, Shield, BarChart3, 
  Clock, Globe, LayoutDashboard, FileText, Settings, 
  TrendingUp, Bot, Users, Smartphone, BellRing 
} from 'lucide-react';

const EurofoxIntegrationHub = () => {
  // Updated data for Bulk SMS focus
  const integrationFeatures = [
    // Left Side Features
    { icon: <Send className="w-5 h-5 md:w-6 md:h-6" />, title: "Flash SMS", linear: "from-blue-500 to-cyan-500", position: "left" },
    { icon: <Shield className="w-5 h-5 md:w-6 md:h-6" />, title: "DLT Compliant", linear: "from-purple-500 to-pink-500", position: "left" },
    { icon: <Settings className="w-5 h-5 md:w-6 md:h-6" />, title: "API Integration", linear: "from-green-500 to-emerald-500", position: "left" },
    { icon: <TrendingUp className="w-5 h-5 md:w-6 md:h-6" />, title: "High ROI", linear: "from-orange-500 to-amber-500", position: "left" },
    { icon: <Users className="w-5 h-5 md:w-6 md:h-6" />, title: "Contact Groups", linear: "from-indigo-500 to-purple-500", position: "left" },
    
    // Right Side Features
    { icon: <Zap className="w-5 h-5 md:w-6 md:h-6" />, title: "Instant Delivery", linear: "from-violet-500 to-purple-500", position: "right" },
    { icon: <Globe className="w-5 h-5 md:w-6 md:h-6" />, title: "Global Reach", linear: "from-cyan-500 to-blue-500", position: "right" },
    { icon: <BarChart3 className="w-5 h-5 md:w-6 md:h-6" />, title: "Real-time Reports", linear: "from-yellow-500 to-orange-500", position: "right" },
    { icon: <Smartphone className="w-5 h-5 md:w-6 md:h-6" />, title: "OTP Services", linear: "from-pink-500 to-rose-500", position: "right" },
    { icon: <BellRing className="w-5 h-5 md:w-6 md:h-6" />, title: "Scheduled SMS", linear: "from-emerald-500 to-teal-500", position: "right" },
  ];

  return (
    <section className="py-24 px-6 bg-slate-50 relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto relative z-10">
        <div className="text-center mb-16 px-4">
          <h1 className="text-3xl md:text-6xl font-semibold text-slate-900 mb-4 tracking-tight -mt-16">
       Grow Faster with {" "}
            <span className="relative inline-block">
              <span className="bg-[#F46653] bg-clip-text text-transparent">
              Eurofox SMS
              </span>
              <motion.div 
                className="absolute -bottom-1 left-0 right-0 h-1 bg-[#F46653] rounded-full" 
                initial={{ scaleX: 0 }} 
                whileInView={{ scaleX: 1 }} 
                viewport={{ once: true }} 
                transition={{ delay: 0.4, duration: 0.8 }} 
              />
            </span>
          </h1>
        
        </div>

        <div className="relative min-h-[500px] lg:min-h-[600px] flex items-center justify-center">
          {/* CENTER ANIMATED BOX */}
          <motion.div 
            initial={{ scale: 0 }} 
            whileInView={{ scale: 1 }} 
            viewport={{ once: true }} 
            transition={{ duration: 0.8, type: "spring" }} 
            className="relative z-20"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-red-500 rounded-3xl blur-2xl opacity-40" />
            <motion.div 
              className="relative bg-[#F46653] rounded-3xl p-8 md:p-12 shadow-2xl border-4 border-white" 
              whileHover={{ scale: 1.05 }}
            >
              <Send className="w-10 h-10 md:w-16 md:h-16 text-white mx-auto mb-4" />
              <h1 className="text-2xl md:text-4xl font-medium text-white text-center tracking-tighter">EUROFOX</h1>
            </motion.div>
            
            {/* Pulsing Rings */}
            {[1, 2, 3].map((ring, idx) => (
              <motion.div 
                key={ring} 
                className="absolute inset-0 border-2 border-orange-400 rounded-3xl" 
                animate={{ scale: [1, 1.3 + idx * 0.2, 1], opacity: [0.6, 0, 0.6] }} 
                transition={{ duration: 4, repeat: Infinity, delay: idx * 0.5, ease: "easeOut" }} 
              />
            ))}
          </motion.div>

          {/* DESKTOP LAYOUT (LEFT & RIGHT) */}
          <div className="hidden lg:block absolute inset-0 w-full h-full -mt-6">
            {/* Left Column */}
            <div className="absolute left-[12%] top-1/2 -translate-y-1/2 space-y-6">
              {integrationFeatures.filter(f => f.position === "left").map((feature, idx) => (
                <motion.div 
                  key={idx} 
                  initial={{ opacity: 0, x: -50 }} 
                  whileInView={{ opacity: 1, x: 0 }} 
                  viewport={{ once: true }} 
                  transition={{ delay: idx * 0.1 }} 
                  whileHover={{ scale: 1.05, x: 10 }} 
                  className="flex items-center gap-4 bg-[#EEF2FC] p-4 rounded-2xl shadow-xl border border-slate-100 min-w-[260px] group cursor-default hover:bg-white"
                >
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${feature.linear} text-white shadow-lg transition-transform group-hover:rotate-12`}>
                    {feature.icon}
                  </div>
                  <h4 className="font-bold text-slate-800 text-lg">{feature.title}</h4>
                </motion.div>
              ))}
            </div>

            {/* Right Column */}
            <div className="absolute right-[12%] top-1/2 -translate-y-1/2 space-y-6">
              {integrationFeatures.filter(f => f.position === "right").map((feature, idx) => (
                <motion.div 
                  key={idx} 
                  initial={{ opacity: 0, x: 50 }} 
                  whileInView={{ opacity: 1, x: 0 }} 
                  viewport={{ once: true }} 
                  transition={{ delay: idx * 0.1 }} 
                  whileHover={{ scale: 1.05, x: -10 }} 
                  className="flex items-center gap-4 bg-[#EEF2FC] p-4 rounded-2xl shadow-xl border border-slate-100 min-w-[260px] group cursor-default hover:bg-white"
                >
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${feature.linear} text-white shadow-lg transition-transform group-hover:-rotate-12`}>
                    {feature.icon}
                  </div>
                  <h4 className="font-bold text-slate-800 text-lg">{feature.title}</h4>
                </motion.div>
              ))}
            </div>
          </div>

          {/* MOBILE LAYOUT (GRID) */}
          <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 mt-20 w-full max-w-2xl">
            {integrationFeatures.map((feature, idx) => (
              <motion.div 
                key={idx} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-md border border-slate-100"
              >
                <div className={`p-2 rounded-lg bg-gradient-to-br ${feature.linear} text-white shadow-sm`}>
                  {feature.icon}
                </div>
                <span className="font-bold text-slate-800 text-sm">{feature.title}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EurofoxIntegrationHub;